'use client'

import { useState } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/ui/dialog'
import { Button } from '@/ui/button'
import { HandleSearch } from './handle-search'
import { createSocialLink } from './actions'
import { useDocumentOps } from '@/document'
import { useQueryClient } from '@tanstack/react-query'

interface HandleSearchResult {
  userId: string
  handle: string
}

interface LinkPersonDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  personId: string
  personName: string
}

export function LinkPersonDialog({
  open,
  onOpenChange,
  personId,
  personName,
}: LinkPersonDialogProps) {
  const [selected, setSelected] = useState<HandleSearchResult | null>(null)
  const [linking, setLinking] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const ops = useDocumentOps()
  const queryClient = useQueryClient()

  const handleLink = async () => {
    if (!selected) return

    setLinking(true)
    setError(null)

    try {
      // Create server-side social link
      const result = await createSocialLink(selected.userId)
      if (!result.success) {
        setError(result.error || 'Failed to create link')
        setLinking(false)
        return
      }

      // Update person entry metadata with linked identity
      ops.updateEntry(personId, {
        metadata: {
          linkedIdentityId: selected.userId,
          linkedHandle: selected.handle,
        },
      })

      // `@/entries` is reached inside this handler, never at module scope. A static import here is
      // an unconditional module-initialisation path `@/social` -> `@/entries` -> `journal/row.tsx`
      // -> `@/feed/row-chrome`, and it throws `Cannot access 'JournalRow' before initialization`
      // into the error boundary on every page that mounts the provider stack —
      // `app/app/layout-client.tsx` imports this face for `InboxProvider`. ADR 0014:108-112, and
      // `feed/row-chrome/index.ts` records the same failure from the other side. The lint zone
      // flags the dynamic form too; this is the one edge it is written to permit.
      // eslint-disable-next-line import/no-restricted-paths
      const { peopleQueryKey } = await import('@/entries')
      queryClient.invalidateQueries({ queryKey: peopleQueryKey })

      setSelected(null)
      onOpenChange(false)
    } catch {
      setError('Failed to link person')
    } finally {
      setLinking(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Link to Konstantout</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-muted-foreground text-sm">
            Search for a Konstantout user to link with {personName}.
          </p>

          <HandleSearch onSelect={setSelected} disabled={linking} />

          {selected && (
            <div className="bg-muted rounded-md p-3">
              <p className="text-sm">
                Link <strong>{personName}</strong> to{' '}
                <strong>@{selected.handle}</strong>?
              </p>
            </div>
          )}

          {error && <p className="text-destructive text-sm">{error}</p>}

          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={linking}
            >
              Cancel
            </Button>
            <Button onClick={handleLink} disabled={!selected || linking}>
              {linking ? 'Linking...' : 'Link'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
